import { useCallback, useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send, ArrowLeft, Search, UserPlus, Move } from 'lucide-react';
import { chatService } from '../services/api';
import { useVisibilityPolling } from '../hooks/useVisibilityPolling';

// Posiciones por defecto (esquina inferior derecha) — se pueden mover
// arrastrando el botón redondo o la cabecera del panel. La posición
// definitiva se guarda en localStorage para persistir entre sesiones.
const DEFAULT_BTN_POS   = { right: 20, bottom: 20 };
const DEFAULT_PANEL_OFF = { right: 20, bottom: 88 };
const BTN_POS_KEY   = 'news:chat:btnPos';
const PANEL_POS_KEY = 'news:chat:panelPos';

// Utilidad: convierte una posición (top/left o right/bottom) en left/top
// absolutos dentro del viewport, respetando límites.
function clampToViewport(x, y, w, h) {
  const maxX = window.innerWidth  - w - 4;
  const maxY = window.innerHeight - h - 4;
  return {
    x: Math.min(Math.max(0, x), maxX),
    y: Math.min(Math.max(0, y), maxY),
  };
}

/** Hook: hace draggable un nodo por un handle, con persistencia. */
function useDraggable(nodeRef, handleRef, storageKey, defaultRight, defaultBottom, size) {
  const [pos, setPos] = useState(null); // {x,y} en left/top absolutos
  const dragRef = useRef({ moving: false, moved: false, sx: 0, sy: 0, ox: 0, oy: 0 });

  // Cargar posición inicial (persistida o default) al montar y en resize
  useEffect(() => {
    const compute = () => {
      const saved = (() => { try { return JSON.parse(localStorage.getItem(storageKey) || 'null'); } catch { return null; } })();
      if (saved && typeof saved.x === 'number' && typeof saved.y === 'number') {
        setPos(clampToViewport(saved.x, saved.y, size.w, size.h));
      } else {
        const x = window.innerWidth  - size.w - defaultRight;
        const y = window.innerHeight - size.h - defaultBottom;
        setPos({ x, y });
      }
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [storageKey, defaultRight, defaultBottom, size.w, size.h]);

  // Handlers de arrastre (mouse + touch)
  useEffect(() => {
    const handle = handleRef.current;
    if (!handle) return;

    const start = (clientX, clientY) => {
      dragRef.current.moving = true;
      dragRef.current.moved = false;
      dragRef.current.sx = clientX;
      dragRef.current.sy = clientY;
      const rect = nodeRef.current.getBoundingClientRect();
      dragRef.current.ox = rect.left;
      dragRef.current.oy = rect.top;
    };
    const move = (clientX, clientY) => {
      if (!dragRef.current.moving) return;
      const dx = clientX - dragRef.current.sx;
      const dy = clientY - dragRef.current.sy;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragRef.current.moved = true;
      const nx = dragRef.current.ox + dx;
      const ny = dragRef.current.oy + dy;
      setPos(clampToViewport(nx, ny, size.w, size.h));
    };
    const end = () => {
      if (!dragRef.current.moving) return;
      dragRef.current.moving = false;
      if (dragRef.current.moved) {
        // Persistir posición final
        const rect = nodeRef.current.getBoundingClientRect();
        try { localStorage.setItem(storageKey, JSON.stringify({ x: rect.left, y: rect.top })); } catch {}
      }
    };

    const onMouseDown = (e) => { start(e.clientX, e.clientY); };
    const onMouseMove = (e) => { move(e.clientX, e.clientY); };
    const onMouseUp   = () => { end(); };
    const onTouchStart = (e) => { const t = e.touches[0]; start(t.clientX, t.clientY); };
    const onTouchMove  = (e) => { const t = e.touches[0]; move(t.clientX, t.clientY); };
    const onTouchEnd   = () => { end(); };

    handle.addEventListener('mousedown', onMouseDown);
    handle.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd);
    return () => {
      handle.removeEventListener('mousedown', onMouseDown);
      handle.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [handleRef, nodeRef, storageKey, size.w, size.h]);

  // Función auxiliar: fue arrastre (bloquea el click posterior)
  const wasDrag = () => dragRef.current.moved;

  return { pos, wasDrag };
}

/**
 * Widget de chat 1-a-1 con botón redondo flotante + panel emergente.
 *
 * Ambos son ARRASTRABLES:
 *  - El botón redondo puede reposicionarse en cualquier zona del viewport.
 *  - El panel también, agarrándolo por su cabecera.
 *  - Las posiciones persisten en localStorage entre sesiones.
 *
 * Props opcionales (para control desde el padre, ej. sidebar admin):
 *  - open: boolean — fuerza el estado del panel desde afuera.
 *  - onOpenChange(bool): callback cuando el usuario abre/cierra.
 *  - onNoLeidosChange(n): callback con el total de no-leídos globales
 *    (útil para mostrar badge fuera del widget, ej. junto a "Mensajes"
 *    en el pie del sidebar).
 */
export default function ChatWidget({ open: openProp, onOpenChange, onNoLeidosChange }) {
  const controlled = openProp !== undefined;
  const [openInner, setOpenInner] = useState(false);
  const abierto = controlled ? openProp : openInner;
  const setAbierto = (v) => {
    if (!controlled) setOpenInner(v);
    onOpenChange?.(v);
  };

  const [noLeidosTotal, setNoLeidosTotal] = useState(0);

  // Vista actual: 'lista' | 'conv' | 'nueva'
  const [vista, setVista] = useState('lista');

  const [conversaciones, setConversaciones] = useState([]);
  const [convActiva, setConvActiva] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);

  const [usuarios, setUsuarios] = useState([]);
  const [busqueda, setBusqueda] = useState('');

  const bottomRef = useRef(null);

  // ── Drag & drop del botón y del panel ──────────────────────────
  const btnRef      = useRef(null);
  const btnHandleRef = useRef(null);
  const panelRef    = useRef(null);
  const panelHandleRef = useRef(null);

  const { pos: btnPos, wasDrag: btnWasDrag } = useDraggable(
    btnRef, btnHandleRef, BTN_POS_KEY,
    DEFAULT_BTN_POS.right, DEFAULT_BTN_POS.bottom,
    { w: 56, h: 56 });

  const { pos: panelPos } = useDraggable(
    panelRef, panelHandleRef, PANEL_POS_KEY,
    DEFAULT_PANEL_OFF.right, DEFAULT_PANEL_OFF.bottom,
    { w: 360, h: 520 });

  // ── Data loaders ───────────────────────────────────────────────
  const refrescarNoLeidos = useCallback(async () => {
    try {
      const n = await chatService.noLeidosTotal();
      setNoLeidosTotal(n);
      onNoLeidosChange?.(n);
    } catch {}
  }, [onNoLeidosChange]);

  const cargarConversaciones = useCallback(async () => {
    try { setConversaciones(await chatService.listarConversaciones()); } catch {}
  }, []);

  const cargarMensajes = useCallback(async (convId) => {
    try {
      const list = await chatService.listarMensajes(convId);
      setMensajes(list);
    } catch {}
  }, []);

  const cargarUsuarios = useCallback(async (q) => {
    try { setUsuarios(await chatService.listarUsuarios(q)); } catch {}
  }, []);

  // ── Polls ──────────────────────────────────────────────────────
  useVisibilityPolling(refrescarNoLeidos, 20000, 60000, true);
  useVisibilityPolling(cargarConversaciones, 20000, 60000,
    abierto && vista === 'lista');
  useVisibilityPolling(
    () => convActiva && cargarMensajes(convActiva.id),
    15000, 45000, abierto && vista === 'conv' && !!convActiva);

  // ── Efectos de arranque ────────────────────────────────────────
  useEffect(() => { refrescarNoLeidos(); }, [refrescarNoLeidos]);

  useEffect(() => {
    if (abierto && vista === 'lista') cargarConversaciones();
  }, [abierto, vista, cargarConversaciones]);

  useEffect(() => {
    if (abierto && vista === 'nueva') cargarUsuarios('');
  }, [abierto, vista, cargarUsuarios]);

  useEffect(() => {
    if (convActiva) {
      cargarMensajes(convActiva.id);
      chatService.marcarLeidos(convActiva.id).catch(() => {}).finally(() => {
        refrescarNoLeidos();
        cargarConversaciones();
      });
    }
  }, [convActiva, cargarMensajes, refrescarNoLeidos, cargarConversaciones]);

  useEffect(() => {
    if (vista === 'conv' && bottomRef.current) {
      bottomRef.current.scrollTop = bottomRef.current.scrollHeight;
    }
  }, [mensajes, vista]);

  // ── Acciones ───────────────────────────────────────────────────
  const abrirConversacionCon = async (usuarioId) => {
    try {
      const conv = await chatService.abrirConversacion(usuarioId);
      setConvActiva(conv);
      setVista('conv');
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const enviarMensaje = async (e) => {
    e?.preventDefault();
    if (!texto.trim() || !convActiva || enviando) return;
    setEnviando(true);
    try {
      const m = await chatService.enviar(convActiva.id, texto.trim());
      setMensajes(prev => [...prev, m]);
      setTexto('');
    } catch (err) {
      alert('Error al enviar: ' + err.message);
    } finally {
      setEnviando(false);
    }
  };

  const fmtFecha = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const hoy = new Date();
    const mismoDia = d.toDateString() === hoy.toDateString();
    return mismoDia
      ? d.toLocaleTimeString('es-CO', { hour:'2-digit', minute:'2-digit' })
      : d.toLocaleDateString('es-CO', { day:'2-digit', month:'2-digit' });
  };

  // ── Render ─────────────────────────────────────────────────────
  return (
    <>
      {/* Botón flotante — arrastrable por sí mismo (todo su cuerpo actúa
          como handle). Al soltar sin haberse movido, es un click normal. */}
      <div
        ref={btnRef}
        style={{
          position:'fixed',
          left: btnPos?.x ?? 0,
          top:  btnPos?.y ?? 0,
          zIndex: 9998,
          visibility: btnPos ? 'visible' : 'hidden',
        }}
      >
        <button
          ref={btnHandleRef}
          onClick={(e) => {
            // Si el mousedown/mouseup implicó arrastre, no togglear
            if (btnWasDrag()) { e.preventDefault(); return; }
            setAbierto(!abierto);
          }}
          title="Chat (arrástrame si estorba)"
          style={{
            width:56, height:56, borderRadius:'50%',
            background:'#1B4332', color:'#fff', border:'none',
            boxShadow:'0 4px 16px rgba(0,0,0,0.2)',
            cursor:'grab',
            display:'flex', alignItems:'center', justifyContent:'center',
            touchAction:'none',
          }}
        >
          {abierto ? <X size={22} /> : <MessageCircle size={22} />}
          {!abierto && noLeidosTotal > 0 && (
            <span style={{
              position:'absolute', top:2, right:2,
              background:'#dc2626', color:'#fff', borderRadius:'50%',
              minWidth:20, height:20, padding:'0 5px',
              fontSize:11, fontWeight:700,
              display:'flex', alignItems:'center', justifyContent:'center',
            }}>{noLeidosTotal > 99 ? '99+' : noLeidosTotal}</span>
          )}
        </button>
      </div>

      {/* Panel — arrastrable por la cabecera */}
      {abierto && (
        <div
          ref={panelRef}
          style={{
            position:'fixed',
            left: panelPos?.x ?? 0,
            top:  panelPos?.y ?? 0,
            width:360, maxWidth:'calc(100vw - 20px)',
            height:520, maxHeight:'calc(100vh - 40px)',
            background:'#fff', borderRadius:12,
            boxShadow:'0 8px 32px rgba(0,0,0,0.2)',
            display:'flex', flexDirection:'column', overflow:'hidden',
            border:'1px solid #e5e7eb',
            zIndex: 9998,
            visibility: panelPos ? 'visible' : 'hidden',
          }}
        >
          {/* Cabecera arrastrable */}
          <div
            ref={panelHandleRef}
            style={{
              display:'flex', alignItems:'center', gap:10,
              padding:'12px 16px', background:'#1B4332', color:'#fff',
              cursor:'grab', userSelect:'none', touchAction:'none',
            }}
          >
            <Move size={14} style={{ opacity: 0.6 }} title="Arrastra para mover" />
            {vista !== 'lista' && (
              <button
                onClick={() => { setVista('lista'); setConvActiva(null); }}
                style={{ background:'transparent', border:'none', color:'#fff', cursor:'pointer', padding:0 }}
                title="Volver"
              ><ArrowLeft size={18} /></button>
            )}
            <span style={{ fontWeight:600, flex:1 }}>
              {vista === 'lista'  && 'Mensajes'}
              {vista === 'conv'   && convActiva?.otroUsuarioNombre}
              {vista === 'nueva'  && 'Nueva conversación'}
            </span>
            {vista === 'lista' && (
              <button
                onClick={() => setVista('nueva')}
                style={{ background:'transparent', border:'none', color:'#fff', cursor:'pointer', padding:0 }}
                title="Iniciar chat con..."
              ><UserPlus size={18} /></button>
            )}
            <button
              onClick={() => setAbierto(false)}
              style={{ background:'transparent', border:'none', color:'#fff', cursor:'pointer', padding:0 }}
              title="Cerrar"
            ><X size={18} /></button>
          </div>

          {/* Cuerpo */}
          {vista === 'lista' && (
            <div style={{ flex:1, overflowY:'auto' }}>
              {conversaciones.length === 0 ? (
                <div style={{ padding:24, color:'#888', textAlign:'center', fontSize:13 }}>
                  Aún no tienes conversaciones. Toca <UserPlus size={14} style={{ verticalAlign:'middle' }} /> para iniciar una.
                </div>
              ) : conversaciones.map(c => (
                <button
                  key={c.id}
                  onClick={() => setConvActiva(c)}
                  style={{
                    display:'flex', alignItems:'center', gap:12, width:'100%',
                    padding:'12px 16px', border:'none', borderBottom:'1px solid #f3f4f6',
                    background:'transparent', cursor:'pointer', textAlign:'left',
                  }}
                >
                  <div style={{
                    width:40, height:40, borderRadius:'50%', background:'#1B4332',
                    color:'#fff', display:'flex', alignItems:'center', justifyContent:'center',
                    fontWeight:600, flexShrink:0,
                  }}>{c.otroUsuarioNombre?.[0]?.toUpperCase() ?? '?'}</div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', gap:8, alignItems:'baseline' }}>
                      <strong style={{ fontSize:13, color:'#1B4332', flex:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                        {c.otroUsuarioNombre}
                      </strong>
                      <span style={{ fontSize:11, color:'#888', flexShrink:0 }}>{fmtFecha(c.ultimoMensajeEn)}</span>
                    </div>
                    <div style={{
                      fontSize:12, color: c.noLeidos > 0 ? '#222' : '#888',
                      fontWeight: c.noLeidos > 0 ? 600 : 400,
                      whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', marginTop:2,
                    }}>
                      {c.ultimoMensaje || 'Sin mensajes'}
                    </div>
                  </div>
                  {c.noLeidos > 0 && (
                    <span style={{
                      background:'#dc2626', color:'#fff', borderRadius:10,
                      padding:'2px 8px', fontSize:11, fontWeight:700, flexShrink:0,
                    }}>{c.noLeidos}</span>
                  )}
                </button>
              ))}
            </div>
          )}

          {vista === 'conv' && (
            <>
              <div ref={bottomRef} style={{ flex:1, overflowY:'auto', padding:'12px 16px', background:'#fafafa' }}>
                {mensajes.length === 0 ? (
                  <div style={{ color:'#888', fontSize:13, textAlign:'center', padding:20 }}>
                    Escribe el primer mensaje.
                  </div>
                ) : mensajes.map(m => {
                  const mio = m.remitenteId !== convActiva?.otroUsuarioId;
                  return (
                    <div key={m.id} style={{
                      display:'flex', justifyContent: mio ? 'flex-end' : 'flex-start', marginBottom:8,
                    }}>
                      <div style={{
                        maxWidth:'75%', padding:'8px 12px', borderRadius:12,
                        background: mio ? '#1B4332' : '#fff', color: mio ? '#fff' : '#222',
                        border: mio ? 'none' : '1px solid #e5e7eb',
                        fontSize:13, lineHeight:1.4, whiteSpace:'pre-wrap',
                      }}>
                        {m.texto}
                        <div style={{
                          fontSize:10, opacity:0.7, marginTop:4, textAlign:'right',
                        }}>{fmtFecha(m.creadoEn)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <form onSubmit={enviarMensaje} style={{
                display:'flex', gap:6, padding:'10px', borderTop:'1px solid #e5e7eb', background:'#fff',
              }}>
                <input
                  type="text"
                  value={texto}
                  onChange={e => setTexto(e.target.value)}
                  placeholder="Escribe un mensaje..."
                  disabled={enviando}
                  style={{
                    flex:1, padding:'8px 12px', border:'1px solid #ddd',
                    borderRadius:20, fontSize:13, outline:'none',
                  }}
                />
                <button
                  type="submit" disabled={!texto.trim() || enviando}
                  style={{
                    background:'#1B4332', color:'#fff', border:'none',
                    width:36, height:36, borderRadius:'50%',
                    cursor: texto.trim() && !enviando ? 'pointer' : 'not-allowed',
                    display:'flex', alignItems:'center', justifyContent:'center',
                    opacity: texto.trim() && !enviando ? 1 : 0.5,
                  }}
                ><Send size={16} /></button>
              </form>
            </>
          )}

          {vista === 'nueva' && (
            <div style={{ flex:1, display:'flex', flexDirection:'column', minHeight:0 }}>
              <div style={{
                display:'flex', alignItems:'center', gap:8,
                padding:'10px 12px', borderBottom:'1px solid #e5e7eb',
              }}>
                <Search size={16} style={{ color:'#888' }} />
                <input
                  type="text"
                  value={busqueda}
                  onChange={e => { setBusqueda(e.target.value); cargarUsuarios(e.target.value); }}
                  placeholder="Buscar usuario..."
                  style={{
                    flex:1, border:'none', outline:'none', fontSize:13,
                  }}
                />
              </div>
              <div style={{ flex:1, overflowY:'auto' }}>
                {usuarios.filter(u => !u.esYo).map(u => (
                  <button
                    key={u.id}
                    onClick={() => abrirConversacionCon(u.id)}
                    style={{
                      display:'flex', alignItems:'center', gap:12, width:'100%',
                      padding:'10px 16px', border:'none', borderBottom:'1px solid #f3f4f6',
                      background:'transparent', cursor:'pointer', textAlign:'left',
                    }}
                  >
                    <div style={{
                      width:36, height:36, borderRadius:'50%', background:'#1B4332',
                      color:'#fff', display:'flex', alignItems:'center', justifyContent:'center',
                      fontWeight:600, fontSize:14, flexShrink:0,
                    }}>{u.nombre?.[0]?.toUpperCase() ?? '?'}</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, color:'#222', fontWeight:500 }}>{u.nombre}</div>
                      {u.email && <div style={{ fontSize:11, color:'#888' }}>{u.email}</div>}
                    </div>
                  </button>
                ))}
                {usuarios.filter(u => !u.esYo).length === 0 && (
                  <div style={{ padding:24, color:'#888', textAlign:'center', fontSize:13 }}>
                    Sin resultados.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
